import { useEffect, useMemo, useState } from 'react';
import { ERROR_PREFIXES } from 'src/common/constants';
import { handleSessionStart } from 'src/services/auth/session/handleSessionStart';
import { useZillowSession } from '../../../hooks/useZillowSession';
import { SessionStatus } from '../../../constants';

export class WithAuthError extends Error {
    type = 'WithAuthError';
    constructor(errorMessage: string) {
        const prefixedMessage = `${ERROR_PREFIXES.APPLICATION_SESSION} ${errorMessage}`;
        super(prefixedMessage);
        this.type = 'WithAuthError';
        this.name = 'WithAuthError';
        this.message = prefixedMessage;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any).__proto__ = WithAuthError.prototype;
    }
}
export const useDecideIfLoginIsRequired = (forceAuth: boolean, serverSideSessionStatus: SessionStatus | null) => {
    const { status, session } = useZillowSession();
    const [hasMadeAuthenticationDecision, setHasMadeAuthenticationDecision] = useState<boolean>(forceAuth);
    const [error, setError] = useState<unknown | null>(null);

    const shouldRedirectToLogin = useMemo(
        () =>
            forceAuth &&
            [serverSideSessionStatus, status].every(
                sessionStatus => sessionStatus === SessionStatus.UNAUTHENTICATED || sessionStatus === null
            ),
        [forceAuth, serverSideSessionStatus, status]
    );
    useEffect(() => {
        const maybeAuthenticate = async () => {
            try {
                if (shouldRedirectToLogin) {
                    handleSessionStart('withAuth determined login is necessary');
                }
            } catch (err) {
                const { datadog } = await import(
                    /* webpackChunkName: "datadog" */
                    'src/services/datadog'
                );
                const error = new WithAuthError(`An error occurred while logging the user in`);
                error.stack = (err as Error).stack;
                datadog.addError(error, {
                    error: (err as Error).message,
                });
                setError(error);
            }
            setHasMadeAuthenticationDecision(false);
        };

        maybeAuthenticate();
    }, [shouldRedirectToLogin, serverSideSessionStatus, status]);

    const isClientSideDecisionPending =
        hasMadeAuthenticationDecision || status === SessionStatus.LOADING || shouldRedirectToLogin;
    const hasServerAuthenticatedUser = serverSideSessionStatus === SessionStatus.AUTHENTICATED;

    const isAuthenticationDecisionPending = !hasServerAuthenticatedUser && isClientSideDecisionPending;
    return { error, session, status, isAuthenticationDecisionPending };
};
